/**
 * DynamicFields — renders a tenant's typed field-definitions as form inputs.
 * Values are a flat { key: stringValue } map; the caller merges them into the
 * record's customAttributes on save. Renders nothing when there are no
 * definitions, so a generic (no-vertical) tenant sees no change.
 *
 * Emits MUI <Grid item> children, so drop it directly inside a <Grid container>.
 *
 * `stacked` puts each label ABOVE its input (the PhoneField / form-section look) instead of
 * MUI's floating label, so a form that mixes both stays visually uniform.
 */
import React from 'react';
import { Box, Grid, TextField, MenuItem, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { FieldDefinition } from '@/services/admin.api';

interface Props {
  definitions: FieldDefinition[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  stacked?: boolean;
}

const StackedLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => (
  <Typography
    component="label"
    sx={{ display: 'block', mb: 0.75, fontSize: '0.8125rem', fontWeight: 600, color: '#334155' }}
  >
    {label}
    {required && (
      <Typography component="span" color="error" ml={0.5}>
        *
      </Typography>
    )}
  </Typography>
);

const DynamicFields: React.FC<Props> = ({ definitions, values, onChange, stacked = false }) => {
  if (!definitions.length) return null;
  return (
    <>
      {definitions.map((d) => {
        const v = values[d.key] ?? '';
        // Floating (default): label lives inside the TextField. Stacked: label above, none inside.
        const labelProps = stacked ? {} : { label: d.label, required: !!d.required };
        const wrap = (control: React.ReactNode) => (
          <Grid item xs={12} sm={6} key={d.key}>
            {stacked ? (
              <Box>
                <StackedLabel label={d.label} required={!!d.required} />
                {control}
              </Box>
            ) : (
              control
            )}
          </Grid>
        );

        if (d.type === 'enum') {
          return wrap(
            <TextField
              select
              fullWidth
              size="small"
              {...labelProps}
              value={v}
              onChange={(e) => onChange(d.key, e.target.value)}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>{stacked ? 'Select…' : '—'}</em>
              </MenuItem>
              {(d.options || []).map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        if (d.type === 'boolean') {
          return (
            <Grid item xs={12} sm={6} key={d.key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={v === 'true'}
                    onChange={(e) => onChange(d.key, e.target.checked ? 'true' : 'false')}
                  />
                }
                label={d.label}
              />
            </Grid>
          );
        }
        const inputType = d.type === 'number' ? 'number' : d.type === 'date' ? 'date' : 'text';
        return wrap(
          <TextField
            fullWidth
            size="small"
            type={inputType}
            {...labelProps}
            value={v}
            onChange={(e) => onChange(d.key, e.target.value)}
            {...(d.type === 'date' && !stacked ? { InputLabelProps: { shrink: true } } : {})}
          />
        );
      })}
    </>
  );
};

export default DynamicFields;
